import React, { useContext } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import { URLS } from '@wix/da-url';
import CoreBadge from '@wix/da-ds/pkg/Badges/CoreBadge';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import EmptyStateBase from '../EmptyState';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import SophisticatedTooltipLink from '@wix/da-ds/pkg/tooltips/SophisticatedTooltipLink';
import SophisticatedTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SophisticatedTooltip';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { PapiUser } from '@wix/da-papi-types';
import EmptyState from '../../../../EmptyState';
import { ShopItemType } from '../../../../../../types/shop';
import CoreExclusive from '../CoreExclusive';

import s from './PremiumContentEmptyState.scss';

export interface Props {
  view: 'wide' | 'large' | 'small';
  user: PapiUser;
  isOwner: boolean;
  hasCoreMembership?: boolean;
}

export const PremiumContentEmptyState: React.FC<Props> = ({
  view,
  isOwner,
  hasCoreMembership,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isPaywallUpdates = useFlag('paywall_updates');

  if (isOwner) {
    if (hasCoreMembership) {
      return isPaywallUpdates ? (
        <EmptyState
          className={classnames(s['root'], s['paywall_updates'], s[view])}
          title={t('pages.shop.emptyState.core.premiumContent.title')}
          titleClassName={s['title']}
          subtitle={
            isMobile
              ? t('pages.shop.emptyState.core.premiumContent.subtitle.mobile')
              : t('pages.shop.emptyState.core.premiumContent.subtitle')
          }
          subtitleClassName={s['subtitle']}
          illustration={<CoreExclusive />}
          illustrationClassName={s['exclusive']}
          withButton={!isMobile}
          buttonLabel={t('common.submit')}
          onButtonClick={() => window.location.replace(URLS.SUBMIT)}
          view={'small'}
          biData={BiData<DefaultBiEvent>({
            evid: 2,
            click_info: 'create_premium_download',
            sectionname: 'premium_download',
          })}
        />
      ) : (
        <EmptyState
          className={classnames(s['root'])}
          title={
            <>
              <SophisticatedTooltip
                size="medium"
                aria-label={t(
                  'pages.shop.emptyState.nonCore.premiumContent.tooltip'
                )}
                description={t(
                  'pages.shop.emptyState.nonCore.premiumContent.tooltip'
                )}
                postfix={
                  <SophisticatedTooltipLink href="https://www.deviantartsupport.com/en/article/what-is-a-core-membership">
                    {t('common.learnMore')}
                  </SophisticatedTooltipLink>
                }
              >
                <CoreBadge size="large" />
              </SophisticatedTooltip>{' '}
              {t('pages.shop.emptyState.core.premiumContent.title')}
            </>
          }
          subtitle={t('pages.shop.emptyState.core.premiumContent.subtitle')}
          subtitleClassName={s['subtitle']}
          withButton
          buttonLabel={t('common.submit')}
          buttonVariant={'approval-green'}
          onButtonClick={() => window.location.replace(URLS.SUBMIT)}
        />
      );
    } else {
      // paywall_updates: clean up translation key as well
      return isPaywallUpdates ? (
        <EmptyState
          className={classnames(s['root'], s['paywall_updates'], s[view])}
          title={t('pages.shop.emptyState.nonCore.premiumContent.title')}
          titleClassName={s['title']}
          subtitle={t(
            isMobile
              ? 'pages.shop.emptyState.nonCore.premiumContent.subtitle__paywall_updates.mobile'
              : 'pages.shop.emptyState.nonCore.premiumContent.subtitle__paywall_updates'
          )}
          subtitleClassName={s['subtitle__paywall_updates']}
          illustration={<CoreExclusive />}
          illustrationClassName={s['exclusive']}
          customButton={
            isMobile ? undefined : (
              <BrandedButton
                size="medium"
                variant="core-orange"
                href={URLS.CORE_MEMBERSHIP}
                biData={{
                  evid: BiEventType.BUY_CORE_BUTTON,
                  referral_info: ReferralType.SHOP_PREMIUM_DOWNLOADS,
                }}
              >
                {t('widgets.tierContent.empty.cta.getCore')}
              </BrandedButton>
            )
          }
          view={'small'}
        />
      ) : (
        <EmptyState
          className={classnames(s['root'])}
          title={t('pages.shop.emptyState.nonCore.premiumContent.title')}
          subtitle={
            <Trans i18nKey="pages.shop.emptyState.nonCore.premiumContent.subtitle">
              <BiLink
                href={URLS.CORE_MEMBERSHIP}
                className={s['upgrade-to-core']}
                biData={{
                  evid: BiEventType.BUY_CORE_BUTTON,
                  referral_info: ReferralType.PAYWALL_PCP,
                }}
              >
                Upgrade to Core{' '}
              </BiLink>
              <SophisticatedTooltip
                size="medium"
                className={s['core-icon']}
                aria-label={t(
                  'pages.shop.emptyState.nonCore.premiumContent.tooltip'
                )}
                description={t(
                  'pages.shop.emptyState.nonCore.premiumContent.tooltip'
                )}
                postfix={
                  <SophisticatedTooltipLink href="https://www.deviantartsupport.com/en/article/what-is-a-core-membership">
                    {t('common.learnMore')}
                  </SophisticatedTooltipLink>
                }
              >
                <CoreBadge size="large" />
              </SophisticatedTooltip>
              to let deviants pay you to unlock your exclusive content.
              <br />
              Add tutorials, works in progress, resources, and more.
            </Trans>
          }
          subtitleClassName={s['subtitle']}
        />
      );
    }
  }

  return (
    <EmptyStateBase shopItemType={ShopItemType.PREMIUM_CONTENT} view="wide" />
  );
};

PremiumContentEmptyState.displayName = 'PremiumContentEmptyState';

export default PremiumContentEmptyState;
